import React, { useCallback } from 'react';
import {
  Grid,
  Button,
  Typography,
  Stack,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { environment } from '../../../environments/environment';
import { handleToaster } from '../../Utils/UtilsFunctions';
// Utility function to convert cents to dollars with proper formatting
import { formatPriceFromCents } from '../../Utils/UtilsFunctions';
import logo from '../../../assets/fattyBattyLogo.png';
const FattyPattyMenuCard = ({ item, handleOpen, bg = '#FFFFFF', storeId }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useSelector((state) => state.authReducer.userData);
  const stores = useSelector((state) => state.shopReducer.stores);

  // Helper function to check if store allows ordering (has pickup or delivery enabled)
  const isStoreOrderingEnabled = useCallback(() => {
    const effectiveStoreId = storeId || user?.defaultStore;

    if (!effectiveStoreId || !stores) {
      return true; // Default to allowing order if we can't determine store status
    }

    const currentStore = stores.find((store) => store.id === effectiveStoreId);

    if (!currentStore) {
      return true; // Default to allowing order if store not found
    }

    // Check if either pickup or delivery is enabled
    const hasPickup = currentStore.pickup_enabled === true;
    const hasDelivery = currentStore.delivery_enabled === true;

    return hasPickup || hasDelivery;
  }, [storeId, user?.defaultStore, stores]);

  // Handle click on menu item - check if store allows ordering
  const handleItemClick = useCallback(
    (itemId, itemData) => {
      if (!isStoreOrderingEnabled()) {
        const message =
          'This store is currently not accepting pickup or delivery orders.';
        // Show an informational toast instead of error/warning as requested
        handleToaster('info', message);
        return;
      }

      handleOpen(itemId, itemData);
    },
    [handleOpen, isStoreOrderingEnabled]
  );
 

  // Helper function to check if a discount is valid for the current store
  // Validates discount based on:
  // - present_at_all_locations: true = applies to all stores
  // - present_at_location_ids: array of specific store IDs where discount is valid
  const isDiscountValidForCurrentStore = useCallback(
    (discount) => {
      const effectiveStoreId = storeId || user?.defaultStore;

      // If no store ID is available, don't show discount
      if (!effectiveStoreId) {
        return false;
      }

      // If discount applies to all locations, it's valid
      if (discount.present_at_all_locations === true) {
        return true;
      }

      // If discount doesn't apply to all locations, check if current store is in the allowed list
      if (
        discount.present_at_location_ids &&
        Array.isArray(discount.present_at_location_ids)
      ) {
        return discount.present_at_location_ids.includes(effectiveStoreId);
      }

      // If no location information is available, don't show discount
      return false;
    },
    [storeId, user?.defaultStore]
  );

  // Check if item has valid discounts for current store
  const hasValidDiscounts =
    item?.discounts &&
    item.discounts.some((discount) => isDiscountValidForCurrentStore(discount));

  // Calculate price range from variations
  const getPriceRange = useCallback(() => {
    const effectiveStoreId = storeId || user?.defaultStore;

    // Compute discounted price by applying valid discount objects to a base price.
    // Mirrors the discount display logic in FattyPattyDialog (discountType + percentage/amountMoney).
    const applyDiscounts = (basePrice) => {
      if (!basePrice || !item?.discounts) return null;
      const validDiscounts = item.discounts.filter((d) => {
        if (!effectiveStoreId) return false;
        if (d.present_at_all_locations === true) return true;
        if (Array.isArray(d.present_at_location_ids)) {
          return d.present_at_location_ids.includes(effectiveStoreId);
        }
        return false;
      });
      if (validDiscounts.length === 0) return null;
      let price = basePrice;
      for (const discount of validDiscounts) {
        if (discount.discountType === 'FIXED_PERCENTAGE') {
          price = price * (1 - (parseFloat(discount.percentage) || 0) / 100);
        } else if (discount.discountType === 'FIXED_AMOUNT') {
          price = Math.max(0, price - (discount.amountMoney?.amount || 0));
        }
      }
      return Math.round(price) !== Math.round(basePrice) ? Math.round(price) : null;
    };

    // Filter variations that are available at the current store
    const availableVariations = item?.variations?.filter((variation) => {
      if (variation.present_at_all_locations === true) return true;
      if (
        effectiveStoreId &&
        variation.present_at_location_ids &&
        Array.isArray(variation.present_at_location_ids)
      ) {
        return variation.present_at_location_ids.includes(effectiveStoreId);
      }
      return true;
    });

    // If no variations or only one variation, return single price
    if (!availableVariations || availableVariations.length === 0) {
      const discountedPrice =
        item?.defaultPriceAfterDiscount || applyDiscounts(item?.price);
      return {
        hasRange: false,
        minPrice: item?.price,
        maxPrice: item?.price,
        minPriceAfterDiscount: discountedPrice,
        maxPriceAfterDiscount: discountedPrice,
      };
    }

    if (availableVariations.length === 1) {
      const variation = availableVariations[0];
      const discountedPrice =
        variation.priceAfterDiscount ||
        item?.defaultPriceAfterDiscount ||
        applyDiscounts(variation.price);
      return {
        hasRange: false,
        minPrice: variation.price,
        maxPrice: variation.price,
        minPriceAfterDiscount: discountedPrice,
        maxPriceAfterDiscount: discountedPrice,
      };
    }

    // Calculate min and max prices from variations
    const prices = availableVariations.map((v) => v.price);
    const pricesAfterDiscount = availableVariations.map(
      (v) =>
        v.priceAfterDiscount ||
        item?.defaultPriceAfterDiscount ||
        applyDiscounts(v.price) ||
        v.price
    );

    return {
      hasRange: true,
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
      minPriceAfterDiscount: Math.min(...pricesAfterDiscount),
      maxPriceAfterDiscount: Math.max(...pricesAfterDiscount),
    };
  }, [item, storeId, user?.defaultStore]);

  const priceRange = getPriceRange();

  // Only show discounted price if discounts are valid for current store
  // AND if there's an actual discount applied (mirrors dialog's fallback logic)
  const shouldShowDiscountedPrice =
    hasValidDiscounts &&
    ((priceRange.hasRange &&
      (priceRange.minPriceAfterDiscount !== priceRange.minPrice ||
        priceRange.maxPriceAfterDiscount !== priceRange.maxPrice)) ||
      (!priceRange.hasRange &&
        priceRange.minPriceAfterDiscount &&
        priceRange.minPriceAfterDiscount !== priceRange.minPrice));

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={4}>
      <div
        style={{
          backgroundColor: bg,
          borderRadius: '20px',
          padding: '20px',
          paddingTop: '80px',
          paddingBottom: '50px',
          textAlign: 'center',
          cursor: 'pointer',

          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          maxHeight: '285px',
          margin: isSmDown ? '40px 8px 80px 8px' : '50px 8px 40px 8px',

          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
        onClick={() => handleItemClick(item?.id, item)}
      >
        {/* Circular Image Container - Half outside the card */}
        <div
          style={{
            position: 'absolute',
            top: '-70px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '161px',
            height: '161px',
            borderRadius: '50%',

            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            border: '10px solid #FFFFFF',
            padding: '10px',
          }}
        >
          <img
            src={item?.item_image_url || logo}
            alt={item?.name}
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '50%',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
            paddingTop: '20px',
            paddingBottom: '40px',
          }}
        >
          <div>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Stack
                sx={{
                  padding: '2px 8px',
                  backgroundColor: '#F0F0F0',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'inline-block',
                }}
                alignItems="center"
                justifyContent="center"
              >
                {shouldShowDiscountedPrice ? (
                  // Display both original and discounted prices (with discount)
                  <div style={{ textAlign: 'center' }}>
                    {priceRange.hasRange ? (
                      // Price range with discount
                      <>
                        <Typography
                          variant="h6"
                          style={{
                            fontFamily: '"Jost", sans-serif',
                            fontWeight: '700',
                            color: '#172B4D',
                            fontSize: '18px',
                            marginBottom: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          {environment.currency}
                          {formatPriceFromCents(
                            priceRange.minPriceAfterDiscount
                          )}
                          <span
                            style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#666666',
                            }}
                          >
                            -
                          </span>
                          {environment.currency}
                          {formatPriceFromCents(
                            priceRange.maxPriceAfterDiscount
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            fontFamily: '"Jost", sans-serif',
                            fontWeight: '400',
                            color: '#999999',
                            fontSize: '13px',
                            textDecoration: 'line-through',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {environment.currency}
                          {formatPriceFromCents(priceRange.minPrice)}
                          <span style={{ fontSize: '11px' }}>-</span>
                          {environment.currency}
                          {formatPriceFromCents(priceRange.maxPrice)}
                        </Typography>
                      </>
                    ) : (
                      // Single price with discount
                      <>
                        <Typography
                          variant="h6"
                          style={{
                            fontFamily: '"Jost", sans-serif',
                            fontWeight: '700',
                            color: '#172B4D',
                            fontSize: '18px',
                            marginBottom: '4px',
                          }}
                        >
                          {environment.currency}
                          {formatPriceFromCents(
                            priceRange.minPriceAfterDiscount
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            fontFamily: '"Jost", sans-serif',
                            fontWeight: '400',
                            color: '#999999',
                            fontSize: '14px',
                            textDecoration: 'line-through',
                          }}
                        >
                          {environment.currency}
                          {formatPriceFromCents(priceRange.minPrice)}
                        </Typography>
                      </>
                    )}
                  </div>
                ) : (
                  // Display regular price (no discount)
                  <div style={{ textAlign: 'center' }}>
                    {priceRange.hasRange ? (
                      // Price range without discount
                      <Typography
                        variant="h6"
                        style={{
                          fontFamily: '"Jost", sans-serif',
                          fontWeight: '700',
                          color: '#172B4D',
                          fontSize: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {environment.currency}
                        {formatPriceFromCents(priceRange.minPrice)}
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#666666',
                          }}
                        >
                          -
                        </span>
                        {environment.currency}
                        {formatPriceFromCents(priceRange.maxPrice)}
                      </Typography>
                    ) : (
                      // Single price without discount
                      <Typography
                        variant="h6"
                        style={{
                          fontFamily: '"Jost", sans-serif',
                          fontWeight: '700',
                          color: '#172B4D',
                          fontSize: '18px',
                        }}
                      >
                        {environment.currency}
                        {formatPriceFromCents(priceRange.minPrice)}
                      </Typography>
                    )}
                  </div>
                )}
              </Stack>
            </Box>

            {/* Item Name */}
            <Typography
              variant="h6"
              title={item?.name}
              style={{
                fontFamily: '"Jost", sans-serif',
                fontWeight: '700',
                color: '#172B4D',
                fontSize: '20px',
                marginBottom: '12px',
                lineHeight: '1.3',
                textTransform: 'capitalize',
                // Clamp long names to 2 lines with ellipsis
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {item?.name}
            </Typography>

            {/* Description */}
            <Typography
              variant="body2"
              style={{
                fontFamily: '"Jost", sans-serif',
                fontWeight: '400',

                color: '#191919',
                fontSize: '14px',
                lineHeight: '1.4',
                textTransform: 'capitalize',
                minHeight: '40px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',

                wordBreak: 'break-word',
                hyphens: 'auto',
              }}
            >
              {item?.description}
            </Typography>
          </div>

          <div style={{ flex: 1 }} />
        </div>

        <Button
          variant="contained"
          style={{
            position: 'absolute',
            bottom: '-25px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: environment.mainColor,
            textShadow:'0 1px 3px rgba(0, 0, 0, 0.3)',  
            color: '#FFFFFF',
            borderRadius: '25px',
            padding: '12px 24px',
            fontFamily: '"Jost", sans-serif',
            fontWeight: '600',
            fontSize: '14px',
            textTransform: 'none',
            boxShadow: `0 4px 15px ${environment.mainColor}4D`,
            border: 'none',
            minWidth: '120px',
            height: '50px',
            transition: 'all 0.3s ease',
            zIndex: 2,
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleItemClick(item?.id, item);
          }}
          onMouseEnter={(e) => {
            // Use a slightly lighter tint on hover derived from mainColor
            e.currentTarget.style.backgroundColor = `${environment.mainColor}CC`;
            e.currentTarget.style.transform =
              'translateX(-50%) translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = environment.mainColor;
            e.currentTarget.style.transform = 'translateX(-50%) translateY(0)';
          }}
        >
          Order Now
        </Button>
      </div>
    </Grid>
  );
};

FattyPattyMenuCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    defaultPriceAfterDiscount: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    description: PropTypes.string,
    item_image_url: PropTypes.string,
    discounts: PropTypes.array,
    variations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        price: PropTypes.number,
        priceAfterDiscount: PropTypes.number,
        currency: PropTypes.string,
        present_at_all_locations: PropTypes.bool,
        present_at_location_ids: PropTypes.array,
        absent_at_location_ids: PropTypes.array,
      })
    ),
  }).isRequired,
  handleOpen: PropTypes.func.isRequired,
  bg: PropTypes.string,
  storeId: PropTypes.string,
};

export default FattyPattyMenuCard;
